import http, { ensureCsrfCookie, releaseLockedRequests } from "../../api/http";

/**
 * État d'authentification gardé uniquement en mémoire.
 *
 * Rien n'est écrit dans localStorage / sessionStorage : à chaque chargement de l'application,
 * la session (cookie HttpOnly) est revérifiée auprès de l'API via GET /user.
 */
export default {
  namespaced: true,
  state: {
    user: null,
    roles: [],
    permissions: [],
    isAuthenticated: false,
    checked: false,
    /** Application verrouillée : seule la saisie du mot de passe permet de reprendre. */
    locked: false,
    /** Verrouillage automatique après ce nombre de minutes d'inactivité (0 = désactivé). */
    lockMinutes: 15,
    /** Modules dont l'utilisateur est validateur désigné (circuits de validation). */
    validations: [],
  },
  mutations: {
    setUser(state, data) {
      state.user = data.user ?? null;
      state.roles = data.roles ?? [];
      state.permissions = data.permissions ?? [];
      state.isAuthenticated = Boolean(data.user);
      state.checked = true;
      state.locked = Boolean(data.verrouille);
      state.lockMinutes = Number.isFinite(Number(data.verrouillage_minutes)) ? Number(data.verrouillage_minutes) : 15;
      state.validations = data.validations ?? [];
    },
    setLocked(state, value) {
      state.locked = Boolean(value) && state.isAuthenticated;
    },
    clearUser(state) {
      releaseLockedRequests(false);
      state.user = null;
      state.roles = [];
      state.permissions = [];
      state.isAuthenticated = false;
      state.checked = true;
      state.locked = false;
      state.validations = [];
    },
    UPDATE_USER_PHOTO(state, photoUrl) {
      if (state.user) {
        state.user = { ...state.user, photo: photoUrl };
      }
    },
    setMustChangePassword(state, value) {
      if (state.user) {
        state.user = { ...state.user, must_change_password: value };
      }
    },
  },
  actions: {
    async login({ dispatch }, credentials) {
      await ensureCsrfCookie(true);
      const { data } = await http.post("login", credentials);
      await dispatch("fetchUser");
      return data.data;
    },

    async fetchUser({ commit }) {
      try {
        const { data } = await http.get("user", { skipAuthRedirect: true });
        commit("setUser", data.data);
        return data.data;
      } catch (error) {
        commit("clearUser");
        throw error;
      }
    },

    /**
     * Vérifie la session une seule fois au démarrage (ou après une déconnexion).
     */
    async ensureSession({ state, dispatch }) {
      if (state.checked) {
        return state.isAuthenticated;
      }
      try {
        await dispatch("fetchUser");
      } catch (error) {
        return false;
      }
      return state.isAuthenticated;
    },

    /** Verrouille l'application (bouton « Verrouiller » ou inactivité). */
    async lock({ commit, state }) {
      if (!state.isAuthenticated || state.locked) return;
      commit("setLocked", true);
      try {
        await http.post("verrouiller");
      } catch (error) {
        // Le verrouillage local suffit à masquer l'écran ; le serveur refusera de toute façon les requêtes suivantes.
      }
    },

    /** Déverrouille avec le mot de passe de l'utilisateur ; renvoie la réponse ou lève l'erreur de l'API. */
    async unlock({ commit }, password) {
      const { data } = await http.post("deverrouiller", { password });
      commit("setLocked", false);
      releaseLockedRequests(true);
      return data;
    },

    async logout({ commit }) {
      try {
        await http.post("logout");
      } catch (error) {
        // La session est déjà invalide côté serveur : on nettoie localement.
      } finally {
        commit("clearUser");
      }
    },
  },
  getters: {
    user: (state) => state.user,
    isAuthenticated: (state) => state.isAuthenticated,
    mustChangePassword: (state) => Boolean(state.user?.must_change_password),
    hasPermission: (state) => (permission) => state.permissions.includes(permission),
    hasAnyPermission: (state) => (permissions) => permissions.some((permission) => state.permissions.includes(permission)),
    hasRole: (state) => (role) => state.roles.includes(role),
    canValidate: (state) => (module) => state.validations.includes(module),
  },
};
